import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Input, Select, Button, message } from 'antd';
import { SearchOutlined, FilterOutlined, HeartOutlined, ShoppingCartOutlined, ArrowUpOutlined, ClearOutlined, EyeOutlined, RobotOutlined, ThunderboltOutlined, TagOutlined, StarOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import CartButton from '../Cart/CartButton';
import FoodCard from '../UI/FoodCard';
import FAB from '../UI/FAB';

// Custom hook for filter management
const useFilters = () => {
    const [filters, setFilters] = useState({
        category_id: '',
        city_id: '',
        search: '',
        vegetarian: false,
        spicy: false,
        sort_by: 'name',
        sort_order: 'asc',
		fast_delivery: false,
		has_offers: false,
		rating_min: '',
        price_min: '',
        price_max: ''
    });

    const updateFilter = useCallback((key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    const clearFilters = useCallback(() => {
        setFilters({
            category_id: '',
            city_id: '',
            search: '',
            vegetarian: false,
            spicy: false,
            sort_by: 'name',
            sort_order: 'asc',
            fast_delivery: false,
            has_offers: false,
            rating_min: '',
            price_min: '',
            price_max: ''
        });
    }, []);

    return { filters, updateFilter, clearFilters };
};

// Custom hook for wishlist management
const useWishlist = (user) => {
    const [wishlist, setWishlist] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadWishlist = useCallback(async () => {
        try {
            if (user) {
                const response = await api.getWishlist().catch(() => ({ success: true, data: [] }));
                if (response?.success) {
                    const items = Array.isArray(response.data) ? response.data : (response.data?.data || []);
                    setWishlist(items.map(item => item.menu_id).filter(Boolean));
                }
            } else {
                const saved = localStorage.getItem('menu_wishlist');
                if (saved) setWishlist(JSON.parse(saved));
            }
        } catch (error) {
            console.warn('Wishlist unavailable');
            setWishlist([]);
        }
    }, [user]);

    const toggleWishlist = useCallback(async (menuId) => {
        setLoading(true);
        try {
            const isInWishlist = wishlist.includes(menuId);
            const newWishlist = isInWishlist 
                ? wishlist.filter(id => id !== menuId)
                : [...wishlist, menuId];
            
            setWishlist(newWishlist);
            
            if (user) {
                await api.updateWishlist(newWishlist);
            } else {
                localStorage.setItem('menu_wishlist', JSON.stringify(newWishlist));
            }
            
            message.success(isInWishlist ? 'Removed from wishlist' : 'Added to wishlist');
        } catch (error) {
            message.error('Failed to update wishlist');
        } finally {
            setLoading(false);
        }
    }, [wishlist, user]);

    return { wishlist, loading, loadWishlist, toggleWishlist };
};

function Menu() {
    const navigate = useNavigate();
    const { user, requireAuth } = useAuth();
    const { addToCart, openCart } = useCart();
    
    // State management
    const [menus, setMenus] = useState([]);
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Custom hooks
    const { filters, updateFilter, clearFilters } = useFilters();
    const { wishlist, loadWishlist, toggleWishlist } = useWishlist(user);
    
    // UI state
    const [collapse, setCollapse] = useState({
        category: true,
        sort: true,
        quick: true,
        price: true,
    });
    
    const toggleSection = useCallback((key) => {
        setCollapse(prev => ({ ...prev, [key]: !prev[key] }));
    }, []);

    // Simplified filter handling
    const handleFilterChange = useCallback((key, value) => {
        updateFilter(key, value);
        setPagination(prev => ({ ...prev, current_page: 1 }));
    }, [updateFilter]);

    // Simplified data fetching
    const fetchData = useCallback(async () => {
        try {
            const [categoriesRes] = await Promise.all([
                api.getCategories().catch(() => ({ data: [] }))
            ]);
            const cats = Array.isArray(categoriesRes?.data) 
                ? categoriesRes.data 
                : (Array.isArray(categoriesRes?.data?.data) ? categoriesRes.data.data : []);
            setCategories(cats);
        } catch (err) {
            console.error('Error fetching data:', err);
        }
    }, []);


    // Optimized menu fetching with memoized params
    const menuParams = useMemo(() => {
            const params = { ...filters, page: pagination.current_page };
        // Clean empty values
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === false) {
                    delete params[key];
                }
            });
        // Map price fields
        if (params.price_min !== undefined) {
                params.min_price = params.price_min;
                delete params.price_min;
            }
        if (params.price_max !== undefined) {
                params.max_price = params.price_max;
                delete params.price_max;
            }
        return params;
    }, [filters, pagination.current_page]);

    const fetchMenus = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.getMenus(menuParams).catch(() => ({ 
                data: { data: [], current_page: 1, last_page: 1 } 
            }));
            
            const list = Array.isArray(response?.data?.data) 
                ? response.data.data 
                : (Array.isArray(response?.data) ? response.data : []);
            
            setMenus(list);
            setPagination({
                current_page: response.data?.current_page || 1,
                last_page: response.data?.last_page || 1,
            });
        } catch (err) {
            setError('Failed to load menus');
            console.error('Error fetching menus:', err);
        } finally {
            setLoading(false);
        }
    }, [menuParams]);

    // Simplified effects
    useEffect(() => {
        loadWishlist();
        fetchData();
    }, [loadWishlist, fetchData]);

    useEffect(() => {
        fetchMenus();
    }, [fetchMenus]);


    // Simplified wishlist check
    const isInWishlist = useCallback((menuId) => wishlist.includes(menuId), [wishlist]);

    const priceBands = [
        { label: '≤ ₹300', min: 0, max: 300 },
        { label: '₹300-500', min: 300, max: 500 },
        { label: '₹500-700', min: 500, max: 700 },
        { label: '₹700-1200', min: 700, max: 1200 },
        { label: '₹1200+', min: 1200, max: '' },
    ];
    
    // Optimized client-side filtering with memoization
    const filteredMenus = useMemo(() => {
        let list = [...menus];
        
 		if (filters.rating_min) {
 			const min = Number(filters.rating_min) || 0;
            list = list.filter(m => (Number(m.rating) || 0) >= min);
 		}
        
 		if (filters.vegetarian) {
            list = list.filter(m => Boolean(m.is_vegetarian));
 		}
        
 		if (filters.fast_delivery) {
            list = list.filter(m => {
 				const prep = Number(m.preparation_time) || Number(m.min_time) || 0;
                return prep > 0 && prep <= 30;
 			});
 		}
        
 		if (filters.has_offers) {
            list = list.filter(m => Boolean(m.offer_text || m.discount_percent));
        }
        
        const minPrice = filters.price_min ? Number(filters.price_min) : undefined;
        const maxPrice = filters.price_max ? Number(filters.price_max) : undefined;
        if (minPrice !== undefined || maxPrice !== undefined) {
            list = list.filter(m => {
 				const price = Number(m.price) || Number(m.starting_price) || 0;
                if (minPrice !== undefined && price < minPrice) return false;
                if (maxPrice !== undefined && price > maxPrice) return false;
 				return true;
 			});
 		}
        
 		return list;
 	}, [menus, filters]);

    const setPriceBand = useCallback((band) => {
        handleFilterChange('price_min', band.min === '' ? '' : band.min);
        handleFilterChange('price_max', band.max === '' ? '' : band.max);
    }, [handleFilterChange]);


    const handleAddToCart = useCallback((menu) => {
        addToCart(menu, 1);
        message.success(`${menu.name} added to cart!`);
    }, [addToCart]);

    const goToPage = useCallback((page) => {
        setPagination(prev => ({ ...prev, current_page: page }));
    }, []);


    // Enhanced skeleton loading with modern gradients
    const loadingGrid = useMemo(() => (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                    <div className="h-48 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse bg-[length:200%_100%]"></div>
                    <div className="p-4 space-y-3">
                        <div className="h-5 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded animate-pulse bg-[length:200%_100%] w-3/4"></div>
                        <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded animate-pulse bg-[length:200%_100%] w-1/2"></div>
                        <div className="flex justify-between items-center">
                            <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded animate-pulse bg-[length:200%_100%] w-16"></div>
                            <div className="h-8 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded-lg animate-pulse bg-[length:200%_100%] w-16"></div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    ), []);

    return (
        <div className="min-h-screen bg-gradient-to-b from-orange-50/30 via-white to-gray-50">
            {/* Cart Button - Fixed Position */}
            <div className="fixed top-3 right-3 sm:top-4 sm:right-4 z-50">
                <CartButton />
            </div>
            
            <div className="min-h-screen bg-[#FAF8F5]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
                {/* Header - Elegant Design */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
                        Our Menu
                    </h1>
                    <p className="text-base md:text-lg text-[#4A4A4A] max-w-2xl mx-auto">
                        Discover delicious dishes from our verified catering partners
                    </p>
                </div>

		{/* Main content: sidebar + results */}
		<div className="grid grid-cols-1 md:grid-cols-12 gap-8">
			{/* Left sidebar: filters/search - Clean Design */}
			<aside className="md:col-span-3">
				<div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 sticky top-20 space-y-6">
					{/* Search Field */}
					<div>
						<Input 
							placeholder="Search dishes..." 
							value={filters.search} 
							onChange={(e) => handleFilterChange('search', e.target.value)}
							className="h-11 rounded-lg border border-gray-300 focus:border-[#C97D60] focus:ring-[#C97D60]"
							size="large"
							prefix={<SearchOutlined className="text-[#4A4A4A]" />}
						/>
					</div>

					{/* Category Filter */}
					<div className="space-y-2">
						<div className="flex items-center gap-2 text-sm font-medium text-[#2C2C2C] mb-2">
							<FilterOutlined className="w-4 h-4" />
							<span>Category</span>
						</div>
						<Select
							className="w-full"
							showSearch
							value={filters.category_id || undefined}
							onChange={(v) => handleFilterChange('category_id', v || '')}
							placeholder="All Categories"
							allowClear
							size="large"
							options={[{ label: 'All Categories', value: '' }, ...categories.map(category => ({ label: category.name, value: String(category.id) }))]}
						/>
					</div>

					{/* Sort Filter */}
					<div className="space-y-2">
						<div className="text-sm font-medium text-[#2C2C2C] mb-2">Sort By</div>
						<Select
							className="w-full"
							value={`${filters.sort_by}-${filters.sort_order}`}
							onChange={(v) => { const [sort_by, sort_order] = v.split('-'); handleFilterChange('sort_by', sort_by); handleFilterChange('sort_order', sort_order); }}
							placeholder="Relevance"
							size="large"
							options={[
								{ label: 'Relevance', value: 'name-asc' },
								{ label: 'Rating', value: 'rating-desc' },
								{ label: 'Time', value: 'preparation_time-asc' },
							]}
						/>
					</div>

					{/* Quick Filters */}
					<div className="space-y-2">
						<div className="text-sm font-medium text-[#2C2C2C] mb-2">Quick Filters</div>
						<div className="flex flex-wrap gap-2">
							<button
								onClick={() => handleFilterChange('vegetarian', !filters.vegetarian)}
								className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
									filters.vegetarian 
										? 'bg-green-500 text-white' 
										: 'bg-gray-100 text-[#4A4A4A] hover:bg-gray-200'
								}`}
							>
								<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
								Veg Only
							</button>
							<button
								onClick={() => handleFilterChange('spicy', !filters.spicy)}
								className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
									filters.spicy 
										? 'bg-red-500 text-white' 
										: 'bg-gray-100 text-[#4A4A4A] hover:bg-gray-200'
								}`}
							>
								<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" /></svg>
								Spicy
							</button>
							<button
								onClick={() => handleFilterChange('fast_delivery', !filters.fast_delivery)}
								className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
									filters.fast_delivery
										? 'bg-[#C97D60] text-white'
										: 'bg-gray-100 text-[#4A4A4A] hover:bg-gray-200'
								}`}
							>
								<ThunderboltOutlined className="w-4 h-4" />
								Fast
							</button>
							<button
								onClick={() => handleFilterChange('rating_min', filters.rating_min ? '' : '4')}
								className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
									filters.rating_min
										? 'bg-[#C97D60] text-white'
										: 'bg-gray-100 text-[#4A4A4A] hover:bg-gray-200'
								}`}
							>
								<StarOutlined className="w-4 h-4" />
								4.0+
							</button>
							<button
								onClick={() => handleFilterChange('has_offers', !filters.has_offers)}
								className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
									filters.has_offers
										? 'bg-[#C97D60] text-white'
										: 'bg-gray-100 text-[#4A4A4A] hover:bg-gray-200'
								}`}
							>
								<TagOutlined className="w-4 h-4" />
								Offers
							</button>
						</div>
					</div>

					{/* Price Filter */}
					<div className="space-y-2">
						<div className="text-sm font-medium text-[#2C2C2C] mb-2">Price Range</div>
						<div className="flex flex-wrap gap-2">
							{priceBands.map((band) => (
								<button
									key={band.label}
									onClick={() => setPriceBand(band)}
									className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
										(filters.price_min == band.min && `${filters.price_max}` == `${band.max}`)
											? 'bg-[#C97D60] text-white'
											: 'bg-gray-100 text-[#4A4A4A] hover:bg-gray-200'
									}`}
								>
									{band.label}
								</button>
							))}
						</div>
					</div>

					{/* Clear Filters */}
					{(filters.search || filters.category_id || filters.vegetarian || filters.spicy || filters.fast_delivery || filters.has_offers || filters.rating_min || filters.price_min || filters.price_max) && (
							<button
								onClick={clearFilters}
							className="w-full px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-orange-100 hover:to-orange-200 hover:text-orange-700 transition-all flex items-center justify-center gap-2 border-2 border-transparent hover:border-orange-300"
							>
							<ClearOutlined className="w-4 h-4" />
							Clear All Filters
							</button>
						)}
					</div>
			</aside>

			{/* Right side: results */}
			<section className="md:col-span-9">
				{/* Sticky bar removed as per request */}

			{/* Results */}
			{loading ? (
                    <div className="px-1">{loadingGrid}</div>
                ) : error ? (
                    <div className="text-center mb-8">
                        <div className="max-w-md mx-auto">
                            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
                                <div className="font-semibold mb-2">Failed to load menus</div>
                                <div className="text-sm mb-3">{error}</div>
                                <Button type="primary" onClick={fetchMenus}>Retry</Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
					<div id="results" className="mb-6"></div>
					
					{/* Applied filters summary chips */}
					{(filters.category_id || filters.vegetarian || filters.spicy || filters.fast_delivery || filters.rating_min || filters.has_offers || filters.price_min || filters.price_max) && (
						<div className="flex flex-wrap gap-2 mb-6 animate-in fade-in duration-300">
						{filters.category_id && (
							<button
								onClick={() => handleFilterChange('category_id', '')}
									className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all flex items-center gap-1.5"
							>
									{categories.find(c => String(c.id) === String(filters.category_id))?.name || 'Category'}
									<ClearOutlined className="w-3 h-3" />
							</button>
						)}
						{filters.vegetarian && (
								<button 
									onClick={() => handleFilterChange('vegetarian', false)} 
									className="px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-all flex items-center gap-1.5"
								>
									🥬 Veg Only
									<ClearOutlined className="w-3 h-3" />
								</button>
						)}
						{filters.spicy && (
								<button 
									onClick={() => handleFilterChange('spicy', false)} 
									className="px-3 py-1.5 rounded-full text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-all flex items-center gap-1.5"
								>
									🌶️ Spicy
									<ClearOutlined className="w-3 h-3" />
								</button>
						)}
						{filters.fast_delivery && (
								<button 
									onClick={() => handleFilterChange('fast_delivery', false)} 
									className="px-3 py-1.5 rounded-full text-sm font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 transition-all flex items-center gap-1.5"
								>
									⚡ Fast Delivery
									<ClearOutlined className="w-3 h-3" />
								</button>
						)}
						{filters.rating_min && (
								<button 
									onClick={() => handleFilterChange('rating_min', '')} 
									className="px-3 py-1.5 rounded-full text-sm font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-all flex items-center gap-1.5"
								>
									⭐ {filters.rating_min}+ Rating
									<ClearOutlined className="w-3 h-3" />
								</button>
						)}
						{filters.has_offers && (
								<button 
									onClick={() => handleFilterChange('has_offers', false)} 
									className="px-3 py-1.5 rounded-full text-sm font-medium bg-violet-100 text-violet-700 hover:bg-violet-200 transition-all flex items-center gap-1.5"
								>
									🏷️ Offers
									<ClearOutlined className="w-3 h-3" />
								</button>
						)}
						{(filters.price_min !== '' || filters.price_max !== '') && (
							<button
								onClick={() => { handleFilterChange('price_min', ''); handleFilterChange('price_max', ''); }}
									className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all flex items-center gap-1.5"
							>
									💰 {filters.price_min !== '' ? `₹${filters.price_min}` : ''}{filters.price_min !== '' && filters.price_max !== '' ? '–' : ''}{filters.price_max !== '' ? `₹${filters.price_max}` : ''}
									<ClearOutlined className="w-3 h-3" />
							</button>
						)}
					</div>
					)}

					<div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
						{filteredMenus.map((menu) => (
							<FoodCard
								key={menu.id}
								variant="menu"
								item={menu}
								imageHeight={180}
								onView={() => navigate(`/menu/${menu.id}`)}
								onAddToCart={handleAddToCart}
								onFavorite={() => toggleWishlist(menu.id)}
								isFavorite={isInWishlist(menu.id)}
								actionsPlacement="footer"
								badges={[
									...(menu.is_vegetarian ? [{ text: 'Veg', color: 'green' }] : []),
									...(menu.is_spicy ? [{ text: 'Spicy', color: 'red' }] : [])
								]}
							/>
						))}
                            </div>
				

					{filteredMenus.length === 0 && !loading && (
                            <div className="text-center py-16">
                                <div className="bg-white rounded-3xl shadow-sm p-12 max-w-md mx-auto">
                                    <div className="text-8xl mb-6">🍽️</div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-3">No dishes found</h3>
                                    <p className="text-gray-600 mb-8 text-lg leading-relaxed">Try adjusting your filters or search terms to discover more options</p>
                                    <Button 
                                        type="primary" 
                                        onClick={clearFilters}
                                        size="large"
                                        className="px-8 py-3 rounded-xl font-medium bg-orange-500 hover:bg-orange-600 h-12 text-base"
                                    >
                                        Clear All Filters
                                    </Button>
                                </div>
                            </div>
                        )}

                        <FAB
                            actions={[
                                {
                                    label: 'Back to top',
                                    title: 'Back to top',
                                    onClick: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
                                    icon: <ArrowUpOutlined className="w-4 h-4" />
                                },
                                {
                                    label: 'Clear filters',
                                    title: 'Clear filters',
                                    onClick: clearFilters,
                                    icon: <ClearOutlined className="w-4 h-4" />
                                },
                                {
                                    label: 'Open cart',
                                    title: 'Open cart',
                                    onClick: openCart,
                                    icon: <ShoppingCartOutlined className="w-4 h-4" />,
                                    bg: '#f97316',
                                    color: '#ffffff'
                                }
                            ]}
                        />
					{pagination.last_page > 1 && (
                            <div className="mt-12 flex items-center justify-center">
                                <div className="flex items-center gap-3">
                                        <button
                                            disabled={pagination.current_page === 1}
                                            onClick={() => goToPage(pagination.current_page - 1)}
                                        className="w-10 h-10 rounded-full bg-white shadow-sm border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                        >
                                        <ArrowUpOutlined className="w-4 h-4 rotate-90" />
                                        </button>
                                    <span className="text-sm text-gray-600 font-medium px-4">
                                            {pagination.current_page} of {pagination.last_page}
                                        </span>
                                        <button
                                            disabled={pagination.current_page === pagination.last_page}
                                            onClick={() => goToPage(pagination.current_page + 1)}
                                        className="w-10 h-10 rounded-full bg-white shadow-sm border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                        >
                                        <ArrowUpOutlined className="w-4 h-4 -rotate-90" />
                                        </button>
                                </div>
                            </div>
                        )}
				</>
			)}
			</section>
			</div>
			</div>
                </div>
            </div>
    );
}

export default Menu;